/**
 * Codes as a person reads them.
 *
 * <p>The database spells things AVAILABLE, UPI_CARD, RETURNED_TO_SUPPLIER,
 * EVENING_COUNTER. Those are fine in a column; they are not fine on a screen that a
 * shop reads all day, and every screen inventing its own translation is how the same
 * value ends up written three ways.
 *
 * <p>Only code-shaped values are touched. Anything with a lower-case letter in it is
 * text somebody typed - a product name, a customer, a brand - and is handed back
 * exactly as it was, because "iPhone 15 Pro" and "OnePlus" are spelt the way they
 * are spelt.
 */

/** Codes whose usual spelling is not a capitalised word. */
const SPECIAL: Record<string, string> = {
  UPI_CARD: 'UPI / Card',
  EMI: 'EMI',
  GST: 'GST',
  IMEI: 'IMEI',
  UPI: 'UPI',
  OWNER: 'Owner',
};

export const readable = (value?: string | number | null): string => {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim();
  if (!raw) return '';

  const special = SPECIAL[raw.toUpperCase()];
  if (special) return special;

  // Already human text. Leave it alone.
  if (/[a-z]/.test(raw)) return raw;

  // Sentence case, not headline case: "Returned to supplier" reads as a state,
  // "Returned To Supplier" reads as a title.
  const words = raw.replace(/[_-]+/g, ' ').toLowerCase().replace(/\s+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Every word capitalised - for names and short labels rather than sentences.
 *
 * <p>Same rule about leaving typed text alone.
 */
export const titleCase = (value?: string | null): string => {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw || /[a-z]/.test(raw)) return raw;
  return raw
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => (SPECIAL[word.toUpperCase()] ? SPECIAL[word.toUpperCase()] : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
};
